import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis 
} from './ui/pagination';
import { toast } from 'sonner';
import { MetadataItem } from './MetadataUploadForm';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface MetadataDeleteConsoleProps {
  metadata: MetadataItem[];
  onDelete: (ids: string[]) => void;
}

export function MetadataDeleteConsole({ metadata, onDelete }: MetadataDeleteConsoleProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<MetadataItem | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(metadata.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMetadata = metadata.slice(startIndex, endIndex);

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleDelete = () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one item to delete');
      return;
    }
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    onDelete(Array.from(selectedIds));
    toast.success(`${selectedIds.size} item(s) deleted successfully`);
    setSelectedIds(new Set());
    setShowDeleteDialog(false);
  };

  const getImageUrl = (fileName: string) => {
    // Mock URL construction - in real app, this would be your actual URL
    return `https://picsum.photos/seed/${fileName}/800/600`;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Delete Console</CardTitle>
          <CardDescription>
            Review and delete metadata entries. Total items: {metadata.length}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-3">
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={selectedIds.size === 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedIds.size})
            </Button>
          </div>

          {metadata.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No metadata entries found
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden mb-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        Select
                      </TableHead>
                      <TableHead>Preview</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Image Type</TableHead>
                      <TableHead>Campaign Types</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>File Name</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedMetadata.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(item.id)}
                            onCheckedChange={() => toggleSelection(item.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <ImageWithFallback
                            src={getImageUrl(item.fileName)}
                            alt={item.displayName}
                            className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setPreviewItem(item)}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{item.displayName}</TableCell>
                        <TableCell>
                          <Badge variant={item.imageType === 'background' ? 'default' : 'secondary'}>
                            {item.imageType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.campaignTypes.map(type => (
                              <Badge key={type} variant="outline">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.categories.map(cat => (
                              <Badge key={cat} variant="outline">
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {item.fileName}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewItem(item)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(Math.max(1, currentPage - 1));
                        }}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={currentPage === page}
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(page);
                              }}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(Math.min(totalPages, currentPage + 1));
                        }}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewItem?.name}</DialogTitle>
            <DialogDescription>
              Review metadata and image before deletion
            </DialogDescription>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-4">
              <ImageWithFallback
                src={getImageUrl(previewItem.fileName)}
                alt={previewItem.displayName}
                className="w-full h-96 object-contain bg-gray-100 rounded"
              />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Display Name</div>
                  <div>{previewItem.displayName}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Image Type</div>
                  <Badge variant={previewItem.imageType === 'background' ? 'default' : 'secondary'}>
                    {previewItem.imageType}
                  </Badge>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Campaign Types</div>
                  <div className="flex flex-wrap gap-1">
                    {previewItem.campaignTypes.map(type => (
                      <Badge key={type} variant="outline">{type}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Categories</div>
                  <div className="flex flex-wrap gap-1">
                    {previewItem.categories.map(cat => (
                      <Badge key={cat} variant="outline">{cat}</Badge>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500 mb-1">File Name</div>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{previewItem.fileName}</code>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500 mb-1">Image URL</div>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded break-all">
                    {getImageUrl(previewItem.fileName)}
                  </code>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} item(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="text-sm text-gray-600 mb-2">Items to be deleted:</div>
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {metadata
                .filter(item => selectedIds.has(item.id))
                .map(item => (
                  <li key={item.id} className="text-sm">
                    • {item.name} ({item.fileName})
                  </li>
                ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete {selectedIds.size} Item(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
